import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  salesBusinessDtoSchema,
  salesBusinessesDtoSchema,
  salesBusinessSessionDtoSchema,
  venueSalesBusinessDtoSchema,
  type SalesBusinessDto,
  type SalesBusinessesDto,
  type SalesBusinessSessionDto,
  type VenueSalesBusinessDto,
} from '@lib/dto/sales-business.dto'
import { bookingDtoSchema, signingLinkDtoSchema, type BookingDto, type SigningLinkDto } from '@lib/dto/sales-booking.dto'
import {
  eventSalesDtoSchema,
  salesPacketDtoSchema,
  sentPacketDtoSchema,
  type EventSalesDto,
  type SalesPacketDto,
  type SentPacketDto,
} from '@lib/dto/sales-packet.dto'
import { paymentIntentDtoSchema, publicPaymentDtoSchema, type PaymentIntentDto, type PublicPaymentDto } from '@lib/dto/sales-payment.dto'
import { salesPriceListDtoSchema, type SalesPriceListDto } from '@lib/dto/sales-price-list.dto'
import {
  salesReadinessDtoSchema,
  salesSchedulesDtoSchema,
  salesSettingsDtoSchema,
  salesTemplatesDtoSchema,
  type SalesReadinessDto,
  type SalesSchedulesDto,
  type SalesSettingsDto,
  type SalesTemplatesDto,
} from '@lib/dto/sales-setup.dto'
import type { CreateAmendmentInput, CreateInvoiceInput, CreatePacketInput, ExtendPacketInput, PacketChoiceInput, SetCountersignerInput } from '@lib/input/sales-packet.input'
import type { DepositReturnInput, OutsidePaymentInput, PaymentIntentInput, RefundInput } from '@lib/input/sales-payment.input'
import type { PutSalesPriceListInput } from '@lib/input/sales-price-list.input'
import type { PutSalesSchedulesInput, PutSalesSettingsInput, PutSalesTemplatesInput } from '@lib/input/sales-setup.input'
import type { ISalesService } from '@/types/SalesService.types'

/** Sales (docs/sales.spec.md): proposals, contracts and payments. */
class SalesServiceImpl implements ISalesService {
  async priceList(venueId: string): Promise<SalesPriceListDto> {
    try {
      return await api.get(`/venues/${venueId}/sales/price-list`, salesPriceListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue’s price list', { venueId }, error)
      throw error
    }
  }

  async savePriceList(venueId: string, input: PutSalesPriceListInput): Promise<SalesPriceListDto> {
    try {
      return await api.put(`/venues/${venueId}/sales/price-list`, input, salesPriceListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save the venue’s price list', { venueId }, error)
      throw error
    }
  }

  async templates(venueId: string): Promise<SalesTemplatesDto> {
    try {
      return await api.get(`/venues/${venueId}/sales/templates`, salesTemplatesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue’s contract templates', { venueId }, error)
      throw error
    }
  }

  async saveTemplates(venueId: string, input: PutSalesTemplatesInput): Promise<SalesTemplatesDto> {
    try {
      return await api.put(`/venues/${venueId}/sales/templates`, input, salesTemplatesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save the venue’s contract templates', { venueId }, error)
      throw error
    }
  }

  async schedules(venueId: string): Promise<SalesSchedulesDto> {
    try {
      return await api.get(`/venues/${venueId}/sales/schedules`, salesSchedulesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue’s payment schedules', { venueId }, error)
      throw error
    }
  }

  async saveSchedules(venueId: string, input: PutSalesSchedulesInput): Promise<SalesSchedulesDto> {
    try {
      return await api.put(`/venues/${venueId}/sales/schedules`, input, salesSchedulesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save the venue’s payment schedules', { venueId }, error)
      throw error
    }
  }

  async settings(venueId: string): Promise<SalesSettingsDto> {
    try {
      return await api.get(`/venues/${venueId}/sales/settings`, salesSettingsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue’s Sales settings', { venueId }, error)
      throw error
    }
  }

  async saveSettings(venueId: string, input: PutSalesSettingsInput): Promise<SalesSettingsDto> {
    try {
      return await api.put(`/venues/${venueId}/sales/settings`, input, salesSettingsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save the venue’s Sales settings', { venueId }, error)
      throw error
    }
  }

  async readiness(venueId: string): Promise<SalesReadinessDto> {
    try {
      return await api.get(`/venues/${venueId}/sales/readiness`, salesReadinessDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load what the venue needs for Sales', { venueId }, error)
      throw error
    }
  }

  async businesses(): Promise<SalesBusinessesDto> {
    try {
      return await api.get('/sales/businesses', salesBusinessesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the businesses that get paid', {}, error)
      throw error
    }
  }

  async venueBusiness(venueId: string): Promise<VenueSalesBusinessDto> {
    try {
      return await api.get(`/venues/${venueId}/sales/business`, venueSalesBusinessDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load who the venue’s payments go to', { venueId }, error)
      throw error
    }
  }

  async createBusiness(venueId: string | null): Promise<SalesBusinessDto> {
    try {
      return await api.post('/sales/businesses', { venueId }, salesBusinessDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to start a business to be paid', { venueId }, error)
      throw error
    }
  }

  async businessSession(businessId: string): Promise<SalesBusinessSessionDto> {
    try {
      return await api.post(`/sales/businesses/${businessId}/session`, {}, salesBusinessSessionDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to open a business’s payment pages', { businessId }, error)
      throw error
    }
  }

  async eventSales(eventId: string): Promise<EventSalesDto> {
    try {
      return await api.get(`/events/${eventId}/sales`, eventSalesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the event’s proposals', { eventId }, error)
      throw error
    }
  }

  async sendPacket(eventId: string, input: CreatePacketInput): Promise<SentPacketDto> {
    try {
      return await api.post(`/events/${eventId}/sales/packets`, input, sentPacketDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to send a proposal', { eventId }, error)
      throw error
    }
  }

  async sendInvoice(eventId: string, input: CreateInvoiceInput): Promise<SalesPacketDto> {
    try {
      return await api.post(`/events/${eventId}/sales/invoices`, input, salesPacketDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to send an invoice', { eventId }, error)
      throw error
    }
  }

  async sendAmendment(eventId: string, input: CreateAmendmentInput): Promise<SentPacketDto> {
    try {
      return await api.post(`/events/${eventId}/sales/amendments`, input, sentPacketDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to send a change to the agreement', { eventId }, error)
      throw error
    }
  }

  async withdrawPacket(eventId: string, packetId: string): Promise<SalesPacketDto> {
    try {
      return await api.post(`/events/${eventId}/sales/packets/${packetId}/withdraw`, {}, salesPacketDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to withdraw a proposal', { eventId, packetId }, error)
      throw error
    }
  }

  async extendPacket(eventId: string, packetId: string, input: ExtendPacketInput): Promise<SalesPacketDto> {
    try {
      return await api.put(`/events/${eventId}/sales/packets/${packetId}/valid-until`, input, salesPacketDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to extend a proposal', { eventId, packetId }, error)
      throw error
    }
  }

  async setCountersigner(eventId: string, packetId: string, input: SetCountersignerInput): Promise<SalesPacketDto> {
    try {
      return await api.put(`/events/${eventId}/sales/packets/${packetId}/countersigner`, input, salesPacketDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to change who countersigns a proposal', { eventId, packetId }, error)
      throw error
    }
  }

  async booking(eventId: string): Promise<BookingDto> {
    try {
      return await api.get(`/events/${eventId}/booking`, bookingDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the proposal', { eventId }, error)
      throw error
    }
  }

  async choosePacket(eventId: string, packetId: string, input: PacketChoiceInput): Promise<BookingDto> {
    try {
      return await api.put(`/events/${eventId}/booking/packets/${packetId}/choice`, input, bookingDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save the couple’s choices', { eventId, packetId }, error)
      throw error
    }
  }

  async openContract(eventId: string, packetId: string): Promise<SigningLinkDto> {
    try {
      return await api.post(`/events/${eventId}/booking/packets/${packetId}/contract`, {}, signingLinkDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to open the contract to sign', { eventId, packetId }, error)
      throw error
    }
  }

  async countersign(eventId: string, packetId: string): Promise<SigningLinkDto> {
    try {
      return await api.post(`/events/${eventId}/sales/packets/${packetId}/countersign`, {}, signingLinkDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to open the contract to countersign', { eventId, packetId }, error)
      throw error
    }
  }

  async recordPayment(eventId: string, paymentId: string, input: OutsidePaymentInput): Promise<EventSalesDto> {
    try {
      return await api.post(`/events/${eventId}/sales/payments/${paymentId}/records`, input, eventSalesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to record a payment', { eventId, paymentId }, error)
      throw error
    }
  }

  async refund(eventId: string, paymentId: string, recordId: string, input: RefundInput): Promise<EventSalesDto> {
    try {
      return await api.post(`/events/${eventId}/sales/payments/${paymentId}/records/${recordId}/refunds`, input, eventSalesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to refund a payment', { eventId, paymentId, recordId }, error)
      throw error
    }
  }

  async returnDeposit(eventId: string, paymentId: string, input: DepositReturnInput): Promise<EventSalesDto> {
    try {
      return await api.post(`/events/${eventId}/sales/payments/${paymentId}/return`, input, eventSalesDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to return a security deposit', { eventId, paymentId }, error)
      throw error
    }
  }

  async publicPayment(token: string): Promise<PublicPaymentDto> {
    try {
      return await api.get(`/pay/${encodeURIComponent(token)}`, publicPaymentDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load a payment', {}, error)
      throw error
    }
  }

  async startAutopayPayment(token: string, input: PaymentIntentInput): Promise<PaymentIntentDto> {
    try {
      return await api.post(`/pay/${encodeURIComponent(token)}/autopay`, input, paymentIntentDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to start a payment that saves the method', {}, error)
      throw error
    }
  }

  async turnOffAutopay(eventId: string): Promise<void> {
    try {
      await api.del(`/events/${eventId}/booking/autopay`)
    } catch (error) {
      logRequestFailure('Failed to switch autopay off', { eventId }, error)
      throw error
    }
  }

  async startPayment(token: string, input: PaymentIntentInput): Promise<PaymentIntentDto> {
    try {
      return await api.post(`/pay/${encodeURIComponent(token)}/intent`, input, paymentIntentDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to start a payment', {}, error)
      throw error
    }
  }
}

export const salesService: ISalesService = new SalesServiceImpl()
